import { createContext } from "react";

import { noop } from "~/utils/helpers";
import { HostContext } from "~/constants";

import { MediaPlayerFullscreenWindowPosition } from "../media/types";

import type { User } from "~/graphql/types";
import type { NoteColor } from "../noteCard/NoteCard";

export interface UserPreferences {
  /** Preferred media playback mode */
  audioOnly: boolean;
  /** Show video or audio captions */
  captionsEnabled: boolean;
  /** Has the user seen the unauthenticated (signed-out) onboarding modal */
  hasOnboarded: boolean;
  /** Has the user seen the signed-in onboarding modal */
  hasOnboardedAuthenticated: boolean;
  /** Has the user seen the transfer modal */
  hasSeenTransferModal: boolean;
  /** Has the user seen the what's new tab */
  hasSeenWhatsNewTab: boolean;
  /** Preferred media playback rate */
  playbackRate: number;
  /** Preferred media volume (Desktop only) */
  volume: number;
  /** Does the user prefer reduced motion */
  reducedMotion: boolean;
  /** Are slides expanded in fullscreen view */
  fullscreenSlides: boolean;
  /** Preferred fullscreen window */
  fullscreenWindowPosition: MediaPlayerFullscreenWindowPosition;
  /** Preferred note color  */
  noteColor: NoteColor;
}

export interface UserContextValue {
  /** The currently logged-in user */
  currentUser: Maybe<Pick<User, "id">>;
  /** The context in which the app is loaded */
  hostContext: HostContext;
  /** User's preferences */
  preferences: UserPreferences;
  /** Navigates to the login page */
  goToLogin: () => void;
  /** Navigates to the registration page */
  goToRegister: () => void;
  /** Invokes the login modal */
  promptLogin: () => void;
  /** Set the value of a user preference */
  setPreference: (
    key: keyof UserPreferences,
    value: UserPreferences[typeof key],
  ) => void;
}

export const initialValue: UserContextValue = {
  currentUser: null,
  hostContext: HostContext.Unknown,
  preferences: {
    audioOnly: false,
    captionsEnabled: false,
    hasOnboarded: false,
    hasOnboardedAuthenticated: false,
    hasSeenTransferModal: false,
    hasSeenWhatsNewTab: false,
    playbackRate: 1,
    volume: 1,
    reducedMotion: false,
    fullscreenSlides: false,
    fullscreenWindowPosition: MediaPlayerFullscreenWindowPosition.bottomLeft,
    noteColor: "cyan",
  },
  goToLogin: noop,
  goToRegister: noop,
  promptLogin: noop,
  setPreference: noop,
};

export const UserContext = createContext<UserContextValue>(initialValue);
